import React, { useRef, useEffect, useState } from "react";
import { View, StyleSheet } from "react-native";
import { useForm, Controller } from "react-hook-form";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import { RichEditor, RichToolbar, actions } from "react-native-pell-rich-editor";
import { File, Remove } from "../../../svgs/CustomCoursework";
import FormLayout from "../common/FormLayout";
import { StoragePathType } from "../../../../graphql/generated/graphql";
import useFileUpload from "../../../../hooks/useFileUpload";

const ResourceForm = ({ activityData, onSave, onCancel, error, isSubmitting, isNewActivity = false }) => {
  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      activityName: activityData?.title || "",
      description: activityData?.description || "",
      videoUrl: activityData?.videoUrl || "",
      files: activityData?.files || [],
    },
  });
  const [uploadError, setUploadError] = useState(null);

  const editorRef = useRef(null);
  const watchActivityName = watch("activityName");
  const watchDescription = watch("description");
  const watchFiles = watch("files");

  // Initialize the editor with the description content
  useEffect(() => {
    if (editorRef.current && activityData?.description) {
      // Set the initial content once when the component mounts
      editorRef.current.setContentHTML(activityData.description);
    }
  }, [activityData]);

  // File upload handling
  const handleAfterUpload = async ({ id, fileName, fileURL, mimeType, size }) => {
    setUploadError(null);
    const newFiles = [
      ...watchFiles,
      {
        fileId: id,
        fileName: fileName,
        fileUrl: fileURL,
        contentType: mimeType,
        size: size,
      },
    ];
    setValue("files", newFiles);
  };

  const { renderFileUpload } = useFileUpload({
    storagePath: StoragePathType.ResourceDocument,
    afterUpload: handleAfterUpload,
    onError: (msg) => setUploadError(msg),
  });

  // Handle removing a file
  const handleFileRemove = (fileToRemove) => {
    const updatedFiles = watchFiles.filter((file) => file !== fileToRemove);
    setValue("files", updatedFiles);
  };

  // Render file list
  const renderFileList = () => {
    return watchFiles.map((file, index) => (
      <View key={index} style={styles.uploadedFile}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <File />
          <CustomSpacing type="horizontal" size="s" />
          <CustomText size="m" weight="ultraBold" text={truncateText(file.fileName, 40)} useTranslationText={false} />
        </View>

        <CustomButton
          leftIcon={<Remove style={{ paddingRight: 4 }} />}
          text="Remove"
          size="s"
          style={{ backgroundColor: "#D0D5DD", borderRadius: 4 }}
          textStyle={{ fontSize: 14, fontWeight: 700 }}
          onPress={() => handleFileRemove(file)}
        />
      </View>
    ));
  };

  // Helper function to truncate long text
  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Form submission
  const onSubmit = (data) => {
    if (!data.activityName.trim()) {
      return;
    }

    // Create the activity data object with all required fields
    const activityData = {
      title: data.activityName,
      description: data.description,
      videoUrl: data.videoUrl || "",
      files: data.files || [],
      type: "Resource",
    };

    onSave(activityData);
  };

  return (
    <FormLayout
      title={`${isNewActivity ? "Create" : "Edit"} Resource`}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
      actionText={isNewActivity ? "Create" : "Save"}
      disableSubmit={!watchActivityName}
      error={error}>
      <CustomText text="Resource Name" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="activityName"
        control={control}
        rules={{ required: true }}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput placeholder="Enter resource name" onBlur={onBlur} onChangeText={onChange} value={value} />
        )}
      />
      {errors.activityName && <CustomText text="Activity name is required" style={styles.errorText} />}

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Description" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="description"
        control={control}
        rules={{ required: false }}
        render={({ field: { onChange, value } }) => (
          <>
            <RichEditor
              style={styles.editor}
              ref={editorRef}
              initialContentHTML={value}
              onChange={(content) => {
                // Directly set the form value when the editor content changes
                onChange(content);
              }}
              placeholder="Enter resource description"
            />
            <RichToolbar
              editor={editorRef}
              actions={[actions.setBold, actions.setItalic, actions.setUnderline, actions.heading1]}
              style={styles.toolbar}
            />
          </>
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="File Upload" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="Upload resource documents for students to access" />
      <CustomSpacing type="vertical" size="s" />
      {renderFileUpload()}
      {uploadError && <CustomText useTranslationText={false} text={uploadError} style={styles.errorText} />}
      {renderFileList()}

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Video URL" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="Paste either a YouTube or Vimeo link here" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="videoUrl"
        control={control}
        rules={{
          validate: (value) => {
            if (!value) return true;
            const isYouTube = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[\w-]+(&[\w=?-]*)*$/.test(
              value.trim()
            );
            const isVimeo = /^(https?:\/\/)?(www\.)?vimeo\.com\/\d+$/.test(value.trim());
            return isYouTube || isVimeo || "Please enter a valid YouTube or Vimeo link.";
          },
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="Paste either a YouTube or Vimeo link here"
            onBlur={onBlur}
            onChangeText={onChange}
            value={value}
          />
        )}
      />
      {errors.videoUrl && (
        <CustomText useTranslationText={false} text={errors.videoUrl.message} style={styles.errorText} />
      )}
    </FormLayout>
  );
};

const styles = StyleSheet.create({
  editor: {
    minHeight: 200,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 4,
  },
  toolbar: {
    backgroundColor: "#f1f1f1",
    borderColor: "#ccc",
    borderTopWidth: 1,
  },
  subText: {
    color: "#667085",
    fontSize: 14,
    fontWeight: "400",
  },
  errorText: {
    color: "#FF0000",
    marginTop: 8,
  },
  uploadedFile: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    marginBottom: 16,
    marginTop: 16,
  },
});

export default ResourceForm;
